/**
 * Bulk Actions Component
 *
 * Provides actions for selected phone numbers.
 */

import { useState } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { usePhoneStore, useSelectedPhoneIds, useCallGroups } from '@/stores/phoneStore';
import { Archive, Tag, X, Trash2 } from 'lucide-react';

// ============================================================================
// Component
// ============================================================================

export function BulkActions() {
  const selectedIds = useSelectedPhoneIds();
  const callGroups = useCallGroups();
  const { bulkUpdatePhones, bulkArchivePhones, deselectAllPhones } = usePhoneStore();
  const { success: showSuccess, error: showError } = useToast();

  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string>('');

  if (selectedIds.length === 0) {
    return null;
  }

  const handleArchive = async () => {
    Logger.info(LogCategory.DATA, 'Bulk archive initiated', 'BulkActions', {
      count: selectedIds.length,
    });
    setIsProcessing(true);

    try {
      await bulkArchivePhones(selectedIds);
      showSuccess('Numbers archived', `${selectedIds.length} phone numbers have been archived.`);
      setShowArchiveConfirm(false);
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Bulk archive failed', 'BulkActions', undefined, error);
      showError('Archive failed', 'Failed to archive phone numbers. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAssignGroup = async () => {
    if (!selectedGroup) {
      showError('No group selected', 'Please select a phone number group.');
      return;
    }

    Logger.info(LogCategory.DATA, 'Bulk assign group initiated', 'BulkActions', {
      count: selectedIds.length,
      group: selectedGroup,
    });
    setIsProcessing(true);

    try {
      await bulkUpdatePhones(selectedIds, { callGroup: selectedGroup === 'unassigned' ? null : selectedGroup });
      showSuccess('Group assigned', `${selectedIds.length} phone numbers have been assigned to ${selectedGroup === 'unassigned' ? 'no group' : selectedGroup}.`);
      setShowAssignModal(false);
      setSelectedGroup('');
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Bulk assign group failed', 'BulkActions', undefined, error);
      showError('Assignment failed', 'Failed to assign phone numbers. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearSelection = () => {
    Logger.debug(LogCategory.UI, 'Selection cleared', 'BulkActions');
    deselectAllPhones();
  };

  return (
    <>
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-4 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary-700">
              {selectedIds.length} phone number{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={handleClearSelection}
              className="text-sm text-primary-600 hover:text-primary-700 underline"
            >
              Clear selection
            </button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Tag className="h-4 w-4" />}
              onClick={() => {
                Logger.debug(LogCategory.UI, 'Assign group modal opened', 'BulkActions');
                setShowAssignModal(true);
              }}
            >
              Assign Group
            </Button>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Archive className="h-4 w-4" />}
              onClick={() => {
                Logger.debug(LogCategory.UI, 'Archive confirm modal opened', 'BulkActions');
                setShowArchiveConfirm(true);
              }}
            >
              Archive
            </Button>
          </div>
        </div>
      </div>

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        onConfirm={handleArchive}
        title="Archive Phone Numbers"
        message={`Are you sure you want to archive ${selectedIds.length} phone number${selectedIds.length !== 1 ? 's' : ''}? Archived numbers will no longer be monitored.`}
        confirmLabel="Archive"
        variant="warning"
        isLoading={isProcessing}
      />

      {/* Assign Group Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-slide-up">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Assign to Group</h2>
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedGroup('');
                }}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                Select a group to assign {selectedIds.length} phone number{selectedIds.length !== 1 ? 's' : ''} to.
              </p>

              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="w-full h-10 px-4 rounded-lg border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="">Select a group...</option>
                <option value="unassigned">Remove from group (Unassigned)</option>
                {callGroups.map((group) => (
                  <option key={group.id} value={group.name}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedGroup('');
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignGroup}
                isLoading={isProcessing}
                disabled={!selectedGroup}
              >
                Assign
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
